import clsx from 'clsx';

import type { GridSize } from '@commons/containers/Grid';
import ResponsiveContainer from '@commons/containers/ResponsiveContainer';
import PageBuilder from '@commons/pagebuilder';
import type { DrupalSection } from '@commons/types/drupal';
import type {
  Alignment,
  Breakpoint,
  ResponsiveStyle,
  Spacing,
  Theme,
  Width,
} from '@commons/types/styles';

import { type DrupalSectionFooter, renderFooterRegion } from '../Footer';
import { type DrupalSectionHeader, renderHeaderRegion } from '../Header';
import { type SectionMinHeightType, getSectionMinHeight } from '../helpers';
import ContentRegion from './Content';
import styles from './index.module.scss';

// Regions of the grid section.

const GRID_SECTION_REGIONS = ['header', 'content', 'footer'];

// The amount of columns the grid section can have.

type GridColumns = '1' | '2' | '3' | '4' | '5';

// Type definition for the GridSection component properties.

type Props = DrupalSection<{
  appearance: {
    theme: Theme;
  };
  container: {
    contentWidth: Width;
    paddingTop: Spacing;
    paddingBottom: Spacing;
    horizontalAlignment: Alignment;
    verticalAlignment: Alignment;
    minHeightType: SectionMinHeightType;
    customMinHeight?: string;
    fluid: boolean;
  };
  layout: {
    columns: GridColumns;
    controlledColumns?: ResponsiveStyle<GridColumns>;
    isResponsiveColumnsControlled?: boolean;
    rowGap: Spacing;
    columnGap: Spacing;
    lastItemSpan?: boolean;
    separator?: boolean;
  };
  header?: DrupalSectionHeader;
  footer?: DrupalSectionFooter;
}>;

/**
 * Grid section component.
 *
 * IMPORTANT:
 * - This component should be a server component, otherwise everything it renders and itself will be client components.
 *   Since it is used on many pages, this would bloat the JavaScript bundle significantly.
 */

const GridSection = ({ type, blocks, weight, settings }: Props) => {
  const { appearance, container, layout, header, footer } = settings;

  const {
    contentWidth,
    paddingTop,
    paddingBottom,
    horizontalAlignment,
    verticalAlignment,
    minHeightType,
    customMinHeight,
    fluid,
  } = container;

  const { rowGap, columnGap, lastItemSpan = false, separator = false } = layout;

  const responsiveContentWidth: ResponsiveStyle<Width> = { xs: 12, lg: contentWidth };

  const minHeight = getSectionMinHeight(minHeightType, customMinHeight);

  const sectionData = { type, weight };
  const blocksByRegions = PageBuilder.filterBlocksByRegions(blocks, GRID_SECTION_REGIONS);

  const renderedHeader =
    header &&
    renderHeaderRegion(blocksByRegions.header, header, sectionData, {
      width: responsiveContentWidth,
    });

  const renderedFooter =
    footer &&
    renderFooterRegion(blocksByRegions.footer, footer, sectionData, {
      width: responsiveContentWidth,
    });

  const renderedContentBlocks = blocksByRegions.content.map((block) =>
    PageBuilder.renderBlock({
      ...block,
      attributes: {
        ...block.attributes,
        classes: clsx(block.attributes.classes, styles.item, 'grid-item'),
      },
    })
  );

  const columns = getResponsiveColumns(layout) as ResponsiveStyle<GridSize>;

  const renderedContentRegion = PageBuilder.renderRegion(
    'content',
    ContentRegion,
    {
      width: responsiveContentWidth,
      columns,
      rowGap,
      columnGap,
      children: renderedContentBlocks,
    },
    sectionData
  );

  const classes = clsx(
    styles.gridSection,
    lastItemSpan && styles.lastItemSpan,
    separator && styles.separator
  );

  const inlineStyles = {
    '--grid-items': blocks.length,
    minHeight,
  };

  return (
    <ResponsiveContainer
      fluid={fluid}
      theme={appearance.theme}
      paddingTop={paddingTop}
      paddingBottom={paddingBottom}
      horizontalAlignment={horizontalAlignment}
      verticalAlignment={verticalAlignment}
      className={classes}
      style={inlineStyles}
    >
      {renderedHeader}
      {renderedContentRegion}
      {renderedFooter}
    </ResponsiveContainer>
  );
};

/**
 * Get the responsive columns object for the grid based on the settings.
 *
 * If the columns are controlled, the default values are overridden by the controlled values.
 *
 * @param settings - The settings of the grid section.
 *
 * @returns The responsive columns object.
 */

const getResponsiveColumns = (settings: Props['settings']['layout']) => {
  const { controlledColumns, isResponsiveColumnsControlled } = settings;

  const columns = parseInt(settings.columns, 10);
  const isColumnsEven = columns % 2 === 0;

  const responsiveColumns = {
    xs: 1,
    sm: 1,
    md: Math.min(columns, 2),
    lg: Math.min(columns, isColumnsEven ? 2 : 3),
    xl: columns,
  };

  if (isResponsiveColumnsControlled && typeof controlledColumns === 'object') {
    for (const [bp, col] of Object.entries(controlledColumns)) {
      const breakpoint = bp as Breakpoint;
      const parsedColumn = parseInt(col, 10);
      responsiveColumns[breakpoint] = parsedColumn;
    }
  }

  return responsiveColumns;
};

export default GridSection;
